import { AxiosError, AxiosResponse } from 'axios';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { isEmpty } from 'lodash';
import config from 'config';
import { encryptPayload } from 'utils/crypto';

interface HttpRequestParams {
  url: string;
  params?: Record<string, unknown>;
  data?: unknown;
  axiosConfig?: AxiosRequestConfig;
}

// Callback type for handling 403 errors
type OnForbiddenCallback = () => void;

class ApiClient {
  private static instance: ApiClient;
  axiosInstance: AxiosInstance;
  private sessionToken: string | null = null;
  private onForbiddenCallback: OnForbiddenCallback | null = null;

  private constructor() {
    this.axiosInstance = axios.create({ baseURL: config.BACKEND_SERVER_URL });

    this.axiosInstance.interceptors.request.use(
      async (config) => {
        const { url } = config;
        if (isEmpty(url)) {
          return Promise.reject({ message: 'ERR_URL_INVALID' });
        }
        // to track request in backend micro services
        config.headers.trace_id = crypto.randomUUID();
        // add application_name
        config.headers.application_name = 'vls-oo-ext-frontend';
        config.headers['Content-Type'] = 'application/json';

        if (this.sessionToken) {
          config.headers.Authorization = `Bearer ${this.sessionToken}`;
        }

        if (['post', 'put', 'get'].includes(config.method || '') && config.data) {
          try {
            // 1. Run the data through our hybrid field-level encryption logic
            const { secureKeyHeader, secureIvHeader, encryptedData } = await encryptPayload(config.data);
            // 2. Map the resulting key strings directly to our target server request headers
            config.headers['X-Secure-Key'] = secureKeyHeader;
            config.headers['X-Secure-IV'] = secureIvHeader;
            // 3. Swap out the request body payload with the encrypted field version
            config.data = encryptedData;
          } catch (cryptoError) {
            console.error('Failed to apply hybrid security headers to the outgoing payload wrapper:', cryptoError);
          }
        }

        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse<object>) => {
        return response;
      },
      (error: AxiosError) => {
        // Handle 401 Unauthorized (e.g. SESSION_BINDING_VIOLATION, expired/invalid
        // token). The backend returns 401 when the session fingerprint no longer
        // matches (e.g. the client's IP changed after idle). Without this,
        // the user stays on a logged-in-looking page while every request silently
        // fails. Degrade gracefully: clear the session and send them to re-login.
        if (error.response?.status === 401) {
          if (this.onForbiddenCallback) {
            this.onForbiddenCallback();
          }
        }

        // Handle 403 Forbidden error
        if (error.response?.status === 403) {
          // Call the registered callback if available
          if (this.onForbiddenCallback) {
            this.onForbiddenCallback();
          }
        }


        if (error.response?.status === 500) {

          error.message = '';
        }

        return Promise.reject(error);
      }
    );
  }

  public static getInstance() {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  //will be called in SessionProvider
  public setSessionToken(token: string | null) {
    this.sessionToken = token;
  }

  public getSessionToken(): string | null {
    return this.sessionToken;
  }

  /**
   * Register a callback to be called when a 403 Forbidden error is encountered
   * This is typically used to navigate to the login page and clear the session
   * @param callback Function to call when 403 error occurs
   */
  public setOnForbiddenCallback(callback: OnForbiddenCallback | null) {
    this.onForbiddenCallback = callback;
  }

  getCancelToken(executor: (cancel: (message?: string) => void) => void) {
    return new axios.CancelToken(executor);
  }

  get({ url, params = {}, axiosConfig = {} }: HttpRequestParams) {
    return this.axiosInstance.get(url, {
      params,
      ...axiosConfig,
    });
  }

  post({ url, data, axiosConfig = {} }: HttpRequestParams) {
    return this.axiosInstance.post(url, data, axiosConfig);
  }

  delete({ url, data = {}, axiosConfig = {} }: HttpRequestParams) {
    return this.axiosInstance.delete(url, {
      data,
      ...axiosConfig,
    });
  }

  put({ url, data = {}, axiosConfig = {} }: HttpRequestParams) {
    return this.axiosInstance.put(url, data, axiosConfig);
  }
}

export default ApiClient;